import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Search, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';
const DEFAULT_DEBOUNCE_MS = 300;
/**
 * A search box that debounces.
 *
 * Three behaviours are deliberate.
 *
 * **Enter flushes immediately.** Someone who types a booking reference and
 * presses Enter has finished; making them wait out the remainder of a debounce
 * feels broken, and is the moment they press Enter again.
 *
 * **Clearing flushes immediately.** Clearing is a request to see everything,
 * and a delay there reads as the filter being stuck.
 *
 * **The callback fires only when the term actually changed.** Typing a
 * character and deleting it within the window would otherwise re-run the
 * query — and with TanStack Query that is a refetch, a spinner and a table
 * that jumps, all for a search that ended where it started.
 */
export function SearchInput({ onSearch, placeholder = 'Search…', defaultValue = '', debounceMs = DEFAULT_DEBOUNCE_MS, className, label = 'Search', disabled = false, }) {
    const id = useId();
    const [value, setValue] = useState(defaultValue);
    const inputRef = useRef(null);
    // The last term handed to `onSearch`, so an edit that returns to where it
    // started does not fire. Seeded with the initial value: the caller has
    // already loaded that result set.
    const lastEmitted = useRef(defaultValue);
    // Held in a ref so the debounce effect does not restart every time the
    // parent re-renders with a new inline callback.
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);
    useEffect(() => {
        if (value === lastEmitted.current)
            return undefined;
        const timer = setTimeout(() => {
            // Re-checked inside the callback, not only before scheduling. Pressing
            // Enter emits the term but does not change `value`, so this effect never
            // re-runs and its cleanup never cancels the timer already in flight —
            // which then fires and searches for the same term a second time.
            if (value === lastEmitted.current)
                return;
            lastEmitted.current = value;
            onSearchRef.current(value);
        }, debounceMs);
        return () => {
            clearTimeout(timer);
        };
    }, [value, debounceMs]);
    const flush = (term) => {
        if (term === lastEmitted.current)
            return;
        lastEmitted.current = term;
        onSearchRef.current(term);
    };
    const handleKeyDown = (event) => {
        if (event.key !== 'Enter')
            return;
        event.preventDefault();
        flush(value);
    };
    const handleClear = () => {
        setValue('');
        flush('');
        inputRef.current?.focus();
    };
    return (_jsxs("div", { className: cn('relative', className), children: [_jsx(Search, { className: "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground", "aria-hidden": "true" }), _jsx("input", { ref: inputRef, id: id, type: "search", role: "searchbox", "aria-label": label, value: value, disabled: disabled, placeholder: placeholder, onChange: (event) => {
                    setValue(event.target.value);
                }, onKeyDown: handleKeyDown, className: "flex h-10 w-full rounded-md border border-input bg-surface pl-9 pr-9 text-sm ring-offset-surface placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" }), value === '' ? null : (_jsx("button", { type: "button", onClick: handleClear, disabled: disabled, "aria-label": "Clear search", className: "absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) }))] }));
}
//# sourceMappingURL=search-input.js.map